// Reading the distinct values of a field: what kind of field it looks like, sorting, filtering and the share of each value.

export interface Seen { value: string; count: number; first: number; last: number }
export type ValueSort = 'common' | 'newest' | 'name';
export const VALUE_SORTS: ValueSort[] = ['common', 'newest', 'name'];
export const VALUE_PAGE = 50;

export type ValueKind = 'constant' | 'category' | 'identifier' | 'numbers' | 'dates' | 'mixed';

export interface ValueSummary {
  distinct: number;
  /** How many times values were seen (a document can carry a value once). */
  total: number;
  kind: ValueKind;
  /** Plain words about the field. */
  insight: string;
  range?: { min: number; max: number };
  topShare: number;
}

const NUMBER = /^-?\d+([.,]\d+)?$/;
const DATE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

export function summarize(values: Seen[]): ValueSummary {
  const distinct = values.length;
  const total = values.reduce((s, v) => s + v.count, 0);
  const top = values.reduce((m, v) => Math.max(m, v.count), 0);
  const topShare = total ? top / total : 0;
  const nums = values.filter((v) => NUMBER.test(v.value.trim()));
  const dates = values.filter((v) => DATE.test(v.value.trim()));
  let kind: ValueKind = 'mixed';
  let range: ValueSummary['range'];
  if (distinct === 1) kind = 'constant';
  else if (distinct > 0 && dates.length === distinct) kind = 'dates';
  else if (distinct > 0 && nums.length === distinct) {
    kind = 'numbers';
    const ns = nums.map((v) => Number(v.value.replace(',', '.')));
    range = { min: Math.min(...ns), max: Math.max(...ns) };
  } else if (distinct > 0 && total > 0 && distinct >= total * 0.9 && distinct > 12) kind = 'identifier';
  else if (distinct <= 15) kind = 'category';
  const insight = {
    constant: 'The same value every time.',
    category: `A category: ${distinct} values, the most common is ${Math.round(topShare * 100)} % of the documents.`,
    identifier: 'Almost every document has its own value: an identifier or a free text.',
    numbers: range ? `Numbers from ${range.min} to ${range.max}.` : 'Numbers.',
    dates: 'Dates or times.',
    mixed: `${distinct} different values; the most common is ${Math.round(topShare * 100)} % of the documents.`,
  }[kind];
  return { distinct, total, kind, insight, range, topShare };
}

export function sortValues(values: Seen[], sort: ValueSort, kind: ValueKind): Seen[] {
  const by: Record<ValueSort, (a: Seen, b: Seen) => number> = {
    common: (a, b) => b.count - a.count || b.last - a.last,
    newest: (a, b) => b.last - a.last || b.count - a.count,
    name: kind === 'numbers'
      ? (a, b) => Number(a.value.replace(',', '.')) - Number(b.value.replace(',', '.'))
      : (a, b) => a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: 'base' }),
  };
  return [...values].sort(by[sort]);
}

export const filterValues = (values: Seen[], text: string): Seen[] => {
  const q = text.trim().toLowerCase();
  return q ? values.filter((v) => v.value.toLowerCase().includes(q)) : values;
};

/** Share of the seen values, 0..1 (the bar). */
export const shareOf = (v: Seen, total: number): number => (total > 0 ? v.count / total : 0);

export const SORT_LABEL: Record<ValueSort, (kind: ValueKind) => string> = {
  common: () => 'Most common',
  newest: () => 'Newest',
  name: (k) => (k === 'numbers' ? 'Lowest first' : 'A → Z'),
};
